import React from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

const tabs = [
  { label: 'Overview', path: '/admin' },
  { label: 'Admin Chat', path: '/admin/chat', adminOnly: true },
  { label: 'Users', path: '/admin/users' },
  { label: 'Invites', path: '/admin/invites', adminOnly: true },
  { label: 'Templates', path: '/admin/templates', adminOnly: true },
  { label: 'Logs', path: '/admin/logs' },
];

export const AdminLayout: React.FC = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;

  const isActive = (tabPath: string) => {
    if (tabPath === '/admin') return currentPath === '/admin';
    return currentPath.startsWith(tabPath);
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-800'}`}>
      {/* Compact admin header */}
      <header className={`px-6 ${darkMode ? 'bg-gray-800 border-b border-gray-700' : 'bg-white border-b border-gray-200'} shadow-sm`}>
        <div className="flex items-center justify-between h-11">
          {/* Left: title */}
          <Link to="/admin" className="flex items-center gap-2 shrink-0 group">
            <div className={`p-1 rounded ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
              Admin
            </span>
          </Link>

          {/* Center: tabs */}
          <nav className="flex items-center gap-0.5 overflow-x-auto flex-nowrap mx-6">
            {tabs
              .filter(tab => !tab.adminOnly || user?.is_superuser)
              .map(tab => {
                const active = isActive(tab.path);
                return (
                  <Link
                    key={tab.path}
                    to={tab.path}
                    className={`px-4 py-3 text-[13px] whitespace-nowrap transition-colors ${
                      active
                        ? darkMode
                          ? 'border-b-[3px] border-red-500 text-white font-semibold bg-red-500/10 rounded-t'
                          : 'border-b-[3px] border-red-500 text-gray-900 font-semibold bg-red-50 rounded-t'
                        : darkMode
                          ? 'border-b-[3px] border-transparent text-gray-500 hover:text-gray-300'
                          : 'border-b-[3px] border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </Link>
                );
              })
            }
          </nav>

          {/* Right: exit */}
          <div className="flex items-center gap-3 shrink-0">
            <span className={`text-xs hidden md:inline ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {user?.email}
            </span>
            <button
              onClick={() => navigate('/')}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                darkMode
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              Exit
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="p-6">
        <Outlet />
      </main>
    </div>
  );
};
